import { Component, Input, Output, EventEmitter, ViewChild, ViewChildren, QueryList, AfterViewInit, HostBinding, inject, computed, signal, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { MatTab } from '@angular/material/tabs';
import { CompanyKeywordService } from '../../../services/companies/company-keyword.service';
import { CompanyKeywordDto } from '../../../models/api/companyKeywordDto';
import { CompanyProcessIndicationDto } from '../../../models/api/companyProcessIndicationDto';
import { CompanyProcessIndicationService } from '../../../services/companies/company-process-indication.service';
import { MaterialModule } from '../../../shared/material.module';
import { CurrentUserState } from '../../../state/current-user.state';
import { setTabYear, setDefaultYear } from '../../../store/actions/company-form.actions';
import { selectTabYear } from '../../../store/selectors/company-form.selectors';
import { selectCompanySearchFilter } from '../../../store/selectors/company-list.selectors';
import { AppUserCompanyAccessType } from '../../../models/enums/appUserCompanyAccessType.enum';
import { UserSessionTrackerService } from '../../../services/user-session-tracker.service';
import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';
import { Category } from '../../../models/enums/category.enum';
import { CreateCompanyRequest } from '../../../models/api/createCompanyRequest';
import { DetailsTabComponent } from './tabs/details-tab/details-tab.component';
import { CollectionTabComponent } from './tabs/collection-tab/collection-tab.component';
import { Audit1TabComponent } from './tabs/audit1-tab/audit1-tab.component';
import { Audit2TabComponent } from './tabs/audit2-tab/audit2-tab.component';
import { BoardTabComponent } from './tabs/board-tab/board-tab.component';
import { CommitteesTabComponent } from './tabs/committees-tab/committees-tab.component';
import { OwnershipTabComponent } from './tabs/ownership-tab/ownership-tab.component';
import { FamilyTabComponent } from './tabs/family-tab/family-tab.component';
import { Salary1TabComponent } from './tabs/salary1-tab/salary1-tab.component';
import { Salary2TabComponent } from './tabs/salary2-tab/salary2-tab.component';
import { AccountingTabComponent } from './tabs/accounting-tab/accounting-tab.component';
import { SocietyTabComponent } from './tabs/society-tab/society-tab.component';
import { EmployeesTabComponent } from './tabs/employees-tab/employees-tab.component';
import { EnergyWaterTabComponent } from './tabs/energy-water-tab/energy-water-tab.component';
import { EmissionsWasteTabComponent } from './tabs/emissions-waste-tab/emissions-waste-tab.component';
import { VotingResultsTabComponent } from './tabs/voting-results-tab/voting-results-tab.component';
import { MessagesTabComponent } from './tabs/messages-tab/messages-tab.component';
import { ProcessTabComponent } from './tabs/process-tab/process-tab.component';

const MAX_TABS = 18;

@Component({
  selector: 'app-company-form',
  imports: [
    MaterialModule,
    DetailsTabComponent,
    CollectionTabComponent,
    Audit1TabComponent,
    Audit2TabComponent,
    BoardTabComponent,
    CommitteesTabComponent,
    OwnershipTabComponent,
    FamilyTabComponent,
    Salary1TabComponent,
    Salary2TabComponent,
    AccountingTabComponent,
    SocietyTabComponent,
    EmployeesTabComponent,
    EnergyWaterTabComponent,
    EmissionsWasteTabComponent,
    VotingResultsTabComponent,
    MessagesTabComponent,
    ProcessTabComponent
  ],
  templateUrl: './company-form.component.html',
  styleUrl: './company-form.component.scss'
})
export class CompanyFormComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() submitButtonText = 'Submit';
  @Input() isEditMode = false;
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() processStatusChange = new EventEmitter<{ year: string; hasData: boolean; categories: { key: string; label: string; shortLabel: string; isChecked: boolean; isFinished: boolean; checkedDate: string | null; checkedByUser: string | null; finishedDate: string | null; finishedByUser: string | null }[] } | null>();

  @ViewChild(DetailsTabComponent) detailsTab?: DetailsTabComponent;
  @ViewChild(CollectionTabComponent) collectionTab?: CollectionTabComponent;
  @ViewChild(Audit1TabComponent) audit1Tab?: Audit1TabComponent;
  @ViewChild(Audit2TabComponent) audit2Tab?: Audit2TabComponent;
  @ViewChild(SocietyTabComponent) societyTab?: SocietyTabComponent;
  @ViewChild(EmployeesTabComponent) employeesTab?: EmployeesTabComponent;
  @ViewChild(EnergyWaterTabComponent) energyWaterTab?: EnergyWaterTabComponent;
  @ViewChild(BoardTabComponent) boardTab?: BoardTabComponent;
  @ViewChild(CommitteesTabComponent) committeesTab?: CommitteesTabComponent;
  @ViewChild(OwnershipTabComponent) ownershipTab?: OwnershipTabComponent;
  @ViewChild(FamilyTabComponent) familyTab?: FamilyTabComponent;
  @ViewChild(Salary1TabComponent) salary1Tab?: Salary1TabComponent;
  @ViewChild(Salary2TabComponent) salary2Tab?: Salary2TabComponent;
  @ViewChild(AccountingTabComponent) accountingTab?: AccountingTabComponent;
  @ViewChild(EmissionsWasteTabComponent) emissionsWasteTab?: EmissionsWasteTabComponent;
  @ViewChild(VotingResultsTabComponent) votingResultsTab?: VotingResultsTabComponent;
  @ViewChild(ProcessTabComponent) processTab?: ProcessTabComponent;
  @ViewChildren(MatTab) tabs!: QueryList<MatTab>;

  @HostBinding('class.compact-enabled') compactEnabled = false;

  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private actions$ = inject(Actions);
  private currentUserState = inject(CurrentUserState);
  private companyKeywordService = inject(CompanyKeywordService);
  private processIndicationService = inject(CompanyProcessIndicationService);
  private sessionTracker = inject(UserSessionTrackerService);
  private yearChangeSubscription: Subscription | null = null;
  selectedTabIndex = signal(0);
  companyKeywords = signal<CompanyKeywordDto[]>([]);
  processStatus = signal<{ year: string; hasData: boolean; categories: { key: string; label: string; shortLabel: string; isChecked: boolean; isFinished: boolean; checkedDate: string | null; checkedByUser: string | null; finishedDate: string | null; finishedByUser: string | null }[] } | null>(null);

  private readonly categoryEnumMap: Record<number, { key: string; label: string; shortLabel: string }> = {
    [Category.Audit]: { key: 'audit', label: 'Audit', shortLabel: 'Aud.' },
    [Category.CorporateGovernance]: { key: 'corporateGovernance', label: 'Corporate Governance', shortLabel: 'CG' },
    [Category.Csr]: { key: 'csr', label: 'ESG', shortLabel: 'ESG' },
    [Category.VotingResults]: { key: 'votingResults', label: 'Voting Results', shortLabel: 'VR' },
    [Category.Compensation]: { key: 'compensation', label: 'Compensation', shortLabel: 'Comp.' },
  };

  private readonly tabCategoryMap: Record<string, { key: string; label: string; shortLabel: string }> = {
    audit1: { key: 'audit', label: 'Audit', shortLabel: 'Aud.' },
    audit2: { key: 'audit', label: 'Audit', shortLabel: 'Aud.' },
    board: { key: 'corporateGovernance', label: 'Corporate Governance', shortLabel: 'CG' },
    committees: { key: 'corporateGovernance', label: 'Corporate Governance', shortLabel: 'CG' },
    ownership: { key: 'corporateGovernance', label: 'Corporate Governance', shortLabel: 'CG' },
    family: { key: 'corporateGovernance', label: 'Corporate Governance', shortLabel: 'CG' },
    salary1: { key: 'compensation', label: 'Compensation', shortLabel: 'Comp.' },
    salary2: { key: 'compensation', label: 'Compensation', shortLabel: 'Comp.' },
    accounting: { key: 'audit', label: 'Audit', shortLabel: 'Aud.' },
    society: { key: 'csr', label: 'ESG', shortLabel: 'ESG' },
    employees: { key: 'csr', label: 'ESG', shortLabel: 'ESG' },
    energyWater: { key: 'csr', label: 'ESG', shortLabel: 'ESG' },
    emissionsWaste: { key: 'csr', label: 'ESG', shortLabel: 'ESG' },
    votingResults: { key: 'votingResults', label: 'Voting Results', shortLabel: 'VR' },
  };

  private processCache: { year: string; data: CompanyProcessIndicationDto } | null = null;
  // Year currently being fetched, so concurrent calls for the same year (e.g. the
  // initial direct call here plus the details tab's initial setTabYear) don't each
  // issue a request before the first response populates processCache.
  private processLoadingYear: string | null = null;

  /** Ordered list of tab keys matching the template order */
  private get tabOrder(): (keyof ReturnType<typeof this.visibleTabs>)[] {
    const hasDetails = this.visibleTabs()['details'];
    return hasDetails
      ? ['details', 'process', 'collection', 'audit1', 'audit2', 'board', 'committees',
         'ownership', 'family', 'salary1', 'salary2', 'accounting', 'society',
         'employees', 'energyWater', 'emissionsWaste', 'votingResults', 'messages']
      : ['collection', 'process', 'audit1', 'audit2', 'board', 'committees',
         'ownership', 'family', 'salary1', 'salary2', 'accounting', 'society',
         'employees', 'energyWater', 'emissionsWaste', 'votingResults', 'messages'];
  }

  visibleTabs = computed(() => ({
    details: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Details),
    collection: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Collection),
    audit1: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Audit1),
    audit2: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Audit2),
    board: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Board),
    committees: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Committees),
    ownership: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Ownership),
    family: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Family),
    salary1: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Salary1),
    salary2: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Salary2),
    accounting: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Accounting),
    society: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Society),
    employees: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Employees),
    energyWater: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.EnergyWater),
    emissionsWaste: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.EmissionsWaste),
    votingResults: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.VotingResults),
    process: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Process),
    messages: this.currentUserState.hasCompanyAccess(AppUserCompanyAccessType.Messages),
  }));

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const tabParam = params.get('tab');
    const yearParam = params.get('year');

    if (tabParam) {
      const index = this.getTabIndex(tabParam);
      if (index >= 0) {
        this.selectedTabIndex.set(index);
      }
    }

    // Get the search measurement year from the company list state — this
    // survives in-app navigation because it lives in a separate state slice.
    const searchYear = this.store.selectSignal(selectCompanySearchFilter)()?.measurement ?? null;

    // A bare `year` query param (no `tab`) means the form was opened directly
    // for that measurement — e.g. "Open in new tab" from the company list,
    // where after a full page load the in-memory search filter is empty. It
    // must seed the default year. With a `tab` param the year instead targets a
    // specific tab (sub-page return), so the search filter still drives default.
    const defaultYear = (yearParam && !tabParam) ? yearParam : searchYear;
    if (defaultYear) {
      this.store.dispatch(setDefaultYear({ year: defaultYear }));
    }

    // If returning from a sub-page with a specific tab+year, set that tab's year
    if (tabParam && yearParam) {
      this.store.dispatch(setTabYear({ tabId: tabParam, year: yearParam }));
    }

    if (this.isEditMode) {
      this.loadKeywords();
      this.startSessionForCurrentTab();
      this.updateProcessStatus();
      this.yearChangeSubscription = this.actions$.pipe(
        ofType(setTabYear)
      ).subscribe(({ tabId, year }) => {
        const activeTabKey = this.getTabKeyFromVisualIndex(this.selectedTabIndex());
        if (tabId === activeTabKey) {
          const companyId = this.route.snapshot.paramMap.get('id');
          this.sessionTracker.startTracking(companyId, tabId, year);
          this.updateProcessStatus();
        }
      });
    }
  }

  private loadKeywords(): void {
    const companyId = this.route.snapshot.paramMap.get('id');
    if (!companyId) return;

    this.companyKeywordService.get(companyId).subscribe({
      next: (keywords) => this.companyKeywords.set(keywords),
      error: (err) => console.error('Keywords load error:', err)
    });
  }

  onKeywordsUpdated(keywords: CompanyKeywordDto[]): void {
    // A tab's save returns only the keyword types it owns; merge by type so
    // other tabs' keywords are preserved in the shared signal (full re-fetch
    // would otherwise be needed to see them again).
    const merged = [...this.companyKeywords()];
    for (const kw of keywords) {
      const idx = merged.findIndex(k => k.type === kw.type);
      if (idx >= 0) {
        merged[idx] = kw;
      } else {
        merged.push(kw);
      }
    }
    this.companyKeywords.set(merged);
  }

  ngOnDestroy(): void {
    this.yearChangeSubscription?.unsubscribe();
  }

  ngAfterViewInit() {
    this.updateCompactState();
    this.tabs.changes.subscribe(() => this.updateCompactState());
  }

  private updateCompactState() {
    this.compactEnabled = this.tabs.length > MAX_TABS - 3;
  }

  hasUnsavedChanges(): boolean {
    const tabComponents: (HasUnsavedChanges | undefined)[] = [
      this.detailsTab,
      this.collectionTab,
      this.audit1Tab,
      this.audit2Tab,
      this.boardTab,
      this.committeesTab,
      this.ownershipTab,
      this.familyTab,
      this.salary1Tab,
      this.salary2Tab,
      this.accountingTab,
      this.societyTab,
      this.employeesTab,
      this.energyWaterTab,
      this.emissionsWasteTab,
      this.votingResultsTab,
      this.processTab,
    ];
    return tabComponents.some(tab => tab?.hasUnsavedChanges());
  }

  getCreateRequest(): CreateCompanyRequest | null {
    return this.detailsTab?.getCreateRequest() ?? null;
  }

  onProcessSaved(): void {
    this.processCache = null;
    this.updateProcessStatus();
  }

  onSubmit() {
    this.submit.emit();
  }

  onCancel() {
    this.cancel.emit();
  }

  onTabIndexChange(index: number): void {
    this.selectedTabIndex.set(index);
    if (this.isEditMode) {
      this.startSessionForCurrentTab();
      this.updateProcessStatus();
    }
  }

  private startSessionForCurrentTab(): void {
    const tabKey = this.getTabKeyFromVisualIndex(this.selectedTabIndex());
    if (!tabKey) return;

    const companyId = this.route.snapshot.paramMap.get('id');
    const year = this.store.selectSignal(selectTabYear(tabKey))()
      ?? this.currentUserState.defaultMeasurementYear();

    this.sessionTracker.startTracking(companyId, tabKey, year);
  }

  private getTabKeyFromVisualIndex(index: number): string | null {
    const visible = this.visibleTabs();
    let currentIndex = 0;
    for (const key of this.tabOrder) {
      const isRendered = key === 'details'
        ? visible[key]
        : this.isEditMode && visible[key];
      if (!isRendered) continue;
      if (currentIndex === index) return key;
      currentIndex++;
    }
    return null;
  }

  updateProcessStatus(): void {
    const tabKey = this.getTabKeyFromVisualIndex(this.selectedTabIndex());
    if (!tabKey) {
      this.setProcessStatus(null);
      return;
    }

    const companyId = this.route.snapshot.paramMap.get('id');
    if (!companyId) {
      this.setProcessStatus(null);
      return;
    }

    const year = this.store.selectSignal(selectTabYear(tabKey))()
      ?? this.currentUserState.defaultMeasurementYear();

    const categoriesToShow = this.getCategoriesToShow(tabKey);
    if (categoriesToShow.length === 0) {
      this.setProcessStatus(null);
      return;
    }

    if (this.processCache && this.processCache.year === year) {
      this.setProcessStatus({
        year, hasData: true,
        categories: categoriesToShow.map(c => ({ ...c, ...this.extractCategoryStatus(this.processCache!.data, c.key) }))
      });
      return;
    }

    // A request for this year is already in flight; skip the duplicate and let the
    // in-flight response populate the status (and the cache for any later callers).
    if (this.processLoadingYear === year) {
      return;
    }

    this.processLoadingYear = year;
    this.processIndicationService.get(companyId, parseInt(year)).subscribe({
      next: resource => {
        this.processLoadingYear = null;
        const data = resource.state === 'present' ? resource.data : null;
        if (data) {
          this.processCache = { year, data };
          this.setProcessStatus({
            year, hasData: true,
            categories: categoriesToShow.map(c => ({ ...c, ...this.extractCategoryStatus(data, c.key) }))
          });
        } else {
          this.processCache = null;
          this.setProcessStatus({
            year, hasData: false,
            categories: categoriesToShow.map(c => ({ ...c, isChecked: false, isFinished: false, checkedDate: null, checkedByUser: null, finishedDate: null, finishedByUser: null }))
          });
        }
      },
      error: () => {
        this.processLoadingYear = null;
        this.processCache = null;
        this.setProcessStatus({
          year, hasData: false,
          categories: categoriesToShow.map(c => ({ ...c, isChecked: false, isFinished: false, checkedDate: null, checkedByUser: null, finishedDate: null, finishedByUser: null }))
        });
      }
    });
  }

  private getCategoriesToShow(tabKey: string): { key: string; label: string; shortLabel: string }[] {
    const tabCategory = this.tabCategoryMap[tabKey];
    if (tabCategory) return [tabCategory];

    const user = this.currentUserState.user();
    if (!user) return [];

    if (user.role === AppUserRole.Admin) {
      return Object.values(this.categoryEnumMap);
    }

    const effectiveCategory = user.category === Category.Accounting ? Category.Audit : user.category;
    const userCategory = this.categoryEnumMap[effectiveCategory];
    return userCategory ? [userCategory] : [];
  }

  private setProcessStatus(status: { year: string; hasData: boolean; categories: { key: string; label: string; shortLabel: string; isChecked: boolean; isFinished: boolean; checkedDate: string | null; checkedByUser: string | null; finishedDate: string | null; finishedByUser: string | null }[] } | null): void {
    this.processStatus.set(status);
    this.processStatusChange.emit(status);
  }

  private extractCategoryStatus(data: CompanyProcessIndicationDto, categoryKey: string): { isChecked: boolean; isFinished: boolean; checkedDate: string | null; checkedByUser: string | null; finishedDate: string | null; finishedByUser: string | null } {
    const cap = categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
    return {
      isChecked: !!data[`is${cap}Checked` as keyof CompanyProcessIndicationDto],
      isFinished: !!data[`is${cap}Finished` as keyof CompanyProcessIndicationDto],
      checkedDate: data[`${categoryKey}CheckedDate` as keyof CompanyProcessIndicationDto] as string | null,
      checkedByUser: data[`${categoryKey}CheckedByUserName` as keyof CompanyProcessIndicationDto] as string | null,
      finishedDate: data[`${categoryKey}FinishedDate` as keyof CompanyProcessIndicationDto] as string | null,
      finishedByUser: data[`${categoryKey}FinishedByUserName` as keyof CompanyProcessIndicationDto] as string | null,
    };
  }

  /**
   * Resolves a tab name (e.g. 'board') to the visible tab index,
   * accounting for hidden tabs due to permissions or create mode.
   */
  private getTabIndex(tabName: string): number {
    const visible = this.visibleTabs();
    let index = 0;
    for (const key of this.tabOrder) {
      // 'details' is always rendered; others require edit mode
      const isRendered = key === 'details'
        ? visible[key]
        : this.isEditMode && visible[key];
      if (key === tabName) {
        return isRendered ? index : -1;
      }
      if (isRendered) {
        index++;
      }
    }
    return -1;
  }
}
